/**
 * The tap sound: a tick on every button press, from the web app itself.
 *
 * Android plays its own touch sound for native views and never for HTML
 * buttons in a WebView, so the app makes one (decision 0010, route A, user
 * 2026-09-02). Web Audio, synthesised: no asset to ship, nothing to cache, and
 * a press IS the user gesture browsers require before they allow sound — so
 * unlike the bell, this can never be muted by "the page has not been touched
 * yet". It is a courtesy, never a gate: a browser with no audio simply stays
 * quiet.
 *
 * Off is a per-tablet choice, kept in this browser like the gun volume
 * override — some docks will want a quiet tablet.
 */

const TAP_SOUND_KEY = 'dockautomation.tapSound'

/** Selectors that count as a button for the tick — the same family the pressed-state scrim uses. */
const TAPPABLE = 'button, [role="button"], .pressable'

export function tapSoundEnabled(): boolean {
  try {
    return localStorage.getItem(TAP_SOUND_KEY) !== 'off'
  } catch {
    return true
  }
}

export function setTapSound(enabled: boolean): void {
  try {
    if (enabled) localStorage.removeItem(TAP_SOUND_KEY)
    else localStorage.setItem(TAP_SOUND_KEY, 'off')
  } catch {
    // No storage: the choice lasts until the next page load.
  }
}

/** The narrowest thing the hook needs from an event target, so the rule is testable without a DOM. */
export type TapTarget = { closest: (selector: string) => { hasAttribute?: (name: string) => boolean, getAttribute?: (name: string) => string | null } | null }

/**
 * Whether a press on `target` should tick.
 *
 * A disabled control gives no feedback — it did nothing, and a click that
 * sounds like it worked is worse than silence. Anything outside a button is
 * silent too: the tick means "that was a control", not "you touched the
 * screen".
 */
export function shouldTick(target: TapTarget | null, enabled = tapSoundEnabled()): boolean {
  if (!enabled || !target) return false
  const control = target.closest(TAPPABLE)
  if (!control) return false
  if (control.hasAttribute?.('disabled')) return false
  if (control.getAttribute?.('aria-disabled') === 'true') return false
  return true
}

// ── The sound ───────────────────────────────────────────────────────────────
//
// One context for the life of the page. The bell opens and closes its own
// because it rings once in a while; a tick can fire ten times a minute, and a
// fresh context per press is both wasteful and audibly late on a tablet.

let context: AudioContext | null = null

function audio(): AudioContext | null {
  if (context) return context
  type AudioWindow = typeof window & { webkitAudioContext?: typeof AudioContext }
  const Ctx = window.AudioContext ?? (window as AudioWindow).webkitAudioContext
  if (!Ctx) return null
  context = new Ctx()
  return context
}

/**
 * A short, bright tick — the shape of Android's own: an instant attack and a
 * decay to nothing inside forty milliseconds. Two partials so it reads as a
 * click rather than a beep. Quiet by design; it sits under speech.
 */
export function playTick(): void {
  const ctx = audio()
  if (!ctx) return
  // A context created by an earlier press can be suspended by the browser
  // when the tab sleeps; resuming inside a gesture is always allowed.
  if (ctx.state === 'suspended') void ctx.resume()

  const now = ctx.currentTime
  const length = 0.04

  const master = ctx.createGain()
  master.gain.setValueAtTime(0.18, now)
  master.gain.exponentialRampToValueAtTime(0.001, now + length)
  master.connect(ctx.destination)

  for (const [frequency, level] of [[2200, 1.0], [3300, 0.35]] as const) {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(frequency, now)
    gain.gain.setValueAtTime(level, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + length)
    osc.connect(gain)
    gain.connect(master)
    osc.start(now)
    osc.stop(now + length)
  }
}
